import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { FetchStatus } from '../../../types/fetch-status.types'
import constants from '../../../constants'
import medicalReimbursementCreateSchema, {
  MedicalReimbursementCreateSchema,
} from '../../../database/validations/MedicalReimbursementCreate'
import { ReimbursementType } from '../../../database/enums/ReimbursementType.enum'
import MedicalReimbursementService from '../../../services/medical-reimbursement'
import { Currency } from '../../../database/enums/Currency.enum'
import { ApprovalStatus } from '../../../database/enums/ApprovalStatus.enum'
import authStore from '../../../store/authStore'

const formEmptyValue: MedicalReimbursementCreateSchema = {
  approvalStatus: ApprovalStatus.UNRESPONDED,
  name: '',
  email: '',
  serviceDate: new Date(),
  reimbursementType: ReimbursementType.CONSULTATION,
  vendor: '',
  description: '',
  totalAmountClaimed: {
    currency: Currency.IDR,
    value: '',
  },
  supportingDocument: null,
}

export default function useLeaveRequestMedicalReimbursementPageViewModel() {
  const user = authStore(state => state.user)
  const [addDataStatus, setAddDataStatus] = useState<FetchStatus>(constants.fetchStatus.default)

  const formHook = useForm<MedicalReimbursementCreateSchema>({
    defaultValues: formEmptyValue,
    mode: 'onChange',
    resolver: zodResolver(medicalReimbursementCreateSchema),
  })

  useEffect(() => {
    if (!!user) {
      formHook.reset({
        ...formEmptyValue,
        name: user.displayName ?? '',
        email: user.email ?? '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  async function onSubmit(data: MedicalReimbursementCreateSchema) {
    setAddDataStatus({ ...constants.fetchStatus.default, isLoading: true })
    await MedicalReimbursementService.add(data)
      .then(() => setAddDataStatus({ ...constants.fetchStatus.default, isSuccess: true }))
      .catch(e => {
        console.log(e)
        setAddDataStatus({ ...constants.fetchStatus.default, isError: true })
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
  }

  function resetPage() {
    formHook.reset({ ...formEmptyValue, name: user?.displayName ?? '', email: user?.email ?? '' })
    setAddDataStatus(constants.fetchStatus.default)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return { formHook, onSubmit, addDataStatus, resetPage }
}
